import { formatBytes, formatDatetime } from "../../../lib/data/values.js";
import { i18n } from "../../../lib/globalization/i18n.js";
import { Services } from "../../../lib/services/Services.js";
import { Component } from "../../../lib/ui/Component.js";
import { Button } from "../../../lib/ui/controls/Button.js";
import { ModalDialog } from "../../../lib/ui/controls/dialogs/ModalDialog.js";
import { Text } from "../../../lib/ui/controls/Text.js";
import { resolveValue } from "../../../lib/ui/utils/ui-helpers.js";
import { FileRepositoryManager } from "../../modules/core/managers/FileRepositoryManager.js";
import { openFileContentInNewWindow } from "../../modules/core/utils/file-content.js";
import {
    FILE_REPOSITORY_PERMISSIONS,
    canOpenFileRepositoryItem,
    canWriteFileRepositoryItems,
} from "../../modules/core/utils/file-repository-permissions.js";

export class WorkingFolderPanel extends Component {

    static PERMISSIONS = FILE_REPOSITORY_PERMISSIONS;

    constructor(parent, options = {}) {

        super(parent, options);

        const {
            getWorkingFolderUid = () => null,
            setWorkingFolderUid = null,
            readOnly = false,
            ensureWorkingFolder = async () => null,
        } = options || {};

        this.getWorkingFolderUidValue = getWorkingFolderUid;
        this.setWorkingFolderUidValue = setWorkingFolderUid;
        this.readOnly = readOnly;
        this.ensureWorkingFolder = ensureWorkingFolder;
        this.fileRepositoryManager = null;

        this.activeFolder = null;
        this.activeAttachments = [];

    };

    getWorkingFolderUid() {
        return resolveValue(this.getWorkingFolderUidValue, this) || null;
    };

    setWorkingFolderUid(workingFolderUid) {

        if (typeof this.setWorkingFolderUidValue !== 'function')
            return;

        this.setWorkingFolderUidValue.call(this, workingFolderUid);

    };

    isReadOnly() { return resolveValue(this.readOnly); }

    getFileRepositoryManager() {

        if (!this.fileRepositoryManager)
            this.fileRepositoryManager = new FileRepositoryManager(this.getApp()?.getAuthManager?.() || null);

        return this.fileRepositoryManager;

    };

    canUserManageFolder() {

        if (this.isReadOnly())
            return false;
        if (!this.activeFolder)
            return true;
        return this.canUserWriteItems();

    };

    canUserOpenFolder() {
        return canOpenFileRepositoryItem(this.activeFolder);
    };

    canUserWriteItems() {
        return canWriteFileRepositoryItems(this.activeFolder);
    };

    canUserDeleteFolder() {
        if (this.isReadOnly())
            return false;
        if (!this.activeFolder)
            return false;
        return this.canUserWriteItems();
    };

    async _refresh(options, currentRoute) {

        await super._refresh(options, currentRoute);
        await Promise.all([
            this.refreshFolder(),
            this.refreshAttachments()
        ]);

    };

    async refreshFolder() {


        const workingFolderUid = this.getWorkingFolderUid();

        if (!workingFolderUid) {
            this.activeFolder = null;
            return;
        }

        const data = await this.getFileRepositoryManager().getFolder(workingFolderUid);

        this.activeFolder = data || null;


    };

    async refreshAttachments() {


        const workingFolderUid = this.getWorkingFolderUid();

        if (!workingFolderUid) {
            this.activeAttachments = [];
            return;
        }

        const dataset = await this.getFileRepositoryManager().getFolderFiles(workingFolderUid);
        const data = (dataset && dataset.data) || [];

        this.activeAttachments = Array.isArray(data)
            ? data
            : [];


    };

    async openAttachment(fileUid) {

        const fileContentUrl = this.getFileRepositoryManager().getFileContentUrl(fileUid);
        return openFileContentInNewWindow(fileUid, fileContentUrl, this.getApp(), (error) => this.handleError(error));

    };

    deleteAttachment(fileUid) {

        const file = (this.activeAttachments || []).find((item) => item?.uid === fileUid);
        if (!file) {
            this.handleError('Soubor nebyl nalezen.');
            return;
        }

        const app = this.getApp();
        const manager = this;

        const modal = new ModalDialog(null, {
            title: i18n.t('module.data_browser.title_record_delete'),
            controls: [
                new Text(null, { text: `Odstranit přílohu "${file.name}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: 'confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.outlined',
                    onClick: deleteCanceled
                }),
            ]
        });

        modal.show(app);

        function deleteCanceled() {
            modal.hide();
        };

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await manager.getFileRepositoryManager().deleteFile(file.uid);
                await manager.refreshFolder();
                await manager.refreshAttachments();
                manager.invalidate();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    deleteWorkingFolder() {

        const folder = this.activeFolder;
        if (!folder?.uid) {
            this.handleError('Pracovní složka nebyla nalezena.');
            return;
        }

        const app = this.getApp();
        const manager = this;

        const modal = new ModalDialog(null, {
            title: i18n.t('module.data_browser.title_record_delete'),
            controls: [
                new Text(null, { text: `Odstranit pracovní složku "${folder.name}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: 'confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.outlined',
                    onClick: deleteCanceled
                }),
            ]
        });

        modal.show(app);

        function deleteCanceled() {
            modal.hide();
        };

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await manager.getFileRepositoryManager().deleteFolder(folder.uid);
                manager.setWorkingFolderUid(null);
                manager.activeFolder = null;
                manager.activeAttachments = [];
                manager.invalidate();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    async addAttachments(files) {

        if (!(files && files.length)) {
            return;
        }

        let workingFolderUid = this.getWorkingFolderUid();

        if (!workingFolderUid) {

            this.getApp().setBusyFlag();

            try {

                const workingFolder = await this.ensureWorkingFolder();
                workingFolderUid = workingFolder?.uid || null;
                this.setWorkingFolderUid(workingFolderUid);
                this.activeFolder = workingFolder || null;

            } catch (error) {

                this.handleError(error);
                return;

            } finally {

                this.getApp().clearBusyFlag();
                this.invalidate();

            }

        }

        if (!workingFolderUid) {
            this.handleError('Nepodařilo se připravit pracovní adresář pro práci s přílohami.');
            return;
        }

        const uploadManager = this.getApp().getUploadPanel();

        files.forEach((file) => {
            uploadManager.uploadFile(file, {
                folderUid: workingFolderUid,
                onSuccess: async () => {
                    await this.refreshFolder();
                    await this.refreshAttachments();
                    this.invalidate();
                }
            });
        });

    };

    invalidate(level = 0) {
        super.invalidate(level);
    }

    _render(vm) {


        const ct = Services.get('templates');
        const attachments = this.activeAttachments || [];

        let controls = [
            ct.nav([
                ct.navgap(),
                (
                    (this.canUserDeleteFolder() && attachments.length === 0)
                        ? ct.button('Odstranit pracovní složku', 'delete', '.text',{
                            title: 'Odstranit pracovní složku',
                            onclick: () => { this.deleteWorkingFolder(); }
                        })
                        : null
                ),
                (
                    this.canUserManageFolder()
                        ? ct.uploadButton('Připojit soubory', 'attachment', {
                            onclick: (e) => {
                                this.addAttachments(e.files);
                            }
                        }, {
                            multiple: true
                        })
                        : null
                ),
            ]),
        ];

        if (!attachments.length) {
            controls.push(
                ct.empty({ title: 'Žádné soubory' })
            );
            return ct.section(controls);
        }

        controls.push(
            ct.table([
                ct.thead([
                    ct.trow([
                        ct.thcell(['Název']),
                        ct.thcell('.right-align', ['Velikost']),
                        ct.thcell('.right-align', ['Vytvořeno']),
                        ct.thcell('.right-align', ['Akce']),
                    ]),
                ]),
                ct.tbody(attachments.map((file) => {
                    return ct.trow([
                        ct.tcell([
                            ct.row({ onclick: () => { this.openAttachment(file.uid); } }, [
                                ct.icon(file.fileTypeIcon || 'file'),
                                ' ',
                                file.name,
                            ]),
                        ]),
                        ct.tcell('.right-align', [
                            formatBytes(file.size) || '-',
                        ]),
                        ct.tcell('.right-align', [
                            formatDatetime(file.createdAt, 'L LT') || '-',
                        ]),
                        ct.tcell('.right-align', [
                            ct.iconButton('open-in-new', '.transparent.circle', {
                                title: 'Otevřít přílohu',
                                onclick: () => { this.openAttachment(file.uid); }
                            }),
                            (
                                this.canUserManageFolder()
                                    ? ct.iconButton('delete', '.transparent.circle', {
                                        title: 'Odstranit přílohu',
                                        onclick: () => { this.deleteAttachment(file.uid); }
                                    })
                                    : null
                            ),
                        ]),
                    ]);
                })),
            ]),
        );


        return ct.section(controls);

    };

}
